import { useCallback, useContext, useEffect, useRef, useState } from "react"

//Soar components
import LoaderFullPage, { LoaderMedium } from "../../../components/Loaders"
import EnterpriseLibrarySidebar from "../../../components/sidebars/EnterpriseLibrarySidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"

// Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { Button } from "primereact/button"
import { TabPanel, TabView } from "primereact/tabview"
import { Dropdown } from "primereact/dropdown"

//Assessments modules
import ManagementForm from "../ManagementForm/ManagementForm"
import {
  assessmentsApiDelete,
  assessmentsApiListPost,
  assessmentsApiActiveOrganizationsGet,
  assessmentsApiGetUsers,
} from "../assessmentsApi"
import AssessmentListHeader from "./AssessmentListHeader"
import AssessmentClone from "./AssessmentClone"

//Helpers
import { LogError } from "../../../helpers/logger"

//Styles
import myStyles from "../../../styles/assessments/AssessmentList.module.css"
import formStyles from "../../../styles/assessments/ManagementForm.module.css"

//GraphQL
import {
  GET_ORGANIZATION,
  GET_ORGANIZATIONS_BY_ENTERPRISE,
  GET_USERS_FROM_LIST,
} from "../queries"
import { useApolloClient, useLazyQuery } from "@apollo/client"
import AssessmentListTable from "./AssessmentListTable"
import AssessmentListFilters from "./AssessmentListFilters"
import AssessmentForm from "../AssessmentForm/AssessmentForm"
import { Tooltip } from "primereact/tooltip"

export default function AssessmentList() {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(false)
  const [tabViewIndex, setTabViewIndex] = useState(0)

  const [selectedOrganization, setSelectedOrganization] = useState({
    organization_id: userCtx.session.user.organization_id,
    organization_alias: userCtx.session.user.organization_alias,
    organization_name: userCtx.session.user.organization_name,
  })
  const [enterpriseOrganizations, setEnterpriseOrganizations] = useState([
    {
      organization_id: userCtx.session.user.organization_id,
      organization_alias: userCtx.session.user.organization_alias,
      organization_name: userCtx.session.user.organization_name,
    },
  ]) //This is a list of organizations within the current enterprise
  const [activeOrganizations, setActiveOrganizations] = useState(undefined) //This is a list of organizations that have AM+ active
  const [sharingOrganizations, setSharingOrganizations] = useState([
    {
      organization_id: userCtx.session.user.organization_id,
      organization_name: userCtx.session.user.organization_name,
    },
  ]) //This is a list of organizations that have shared assessments

  const [assessmentData, setAssessmentData] = useState(undefined)

  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [users, setUsers] = useState({
    ownedUserIds: [],
    sharedUserIds: [],
    systemUserIds: [],
  })
  const [pageSetup, setPageSetup] = useState({ rows: 20, first: 0, page: 0 })
  const [editAssessmentId, setEditAssessmentId] = useState(undefined)
  const [previewAssessmentId, setPreviewAssessmentId] = useState(undefined)
  const [showFormDialog, setShowFormDialog] = useState(false)
  const [showFilterDialog, setShowFilterDialog] = useState(false)
  const [cloneAssessment, setCloneAssessment] = useState(undefined)
  const [listFilters, setListFilters] = useState({
    enterpriseId:
      selectedOrganization.organization_id ===
      userCtx.session.user.enterprise_id
        ? userCtx.session.user.enterprise_id
        : undefined,
    organizationId: selectedOrganization.organization_id,
    activeAssessments: true,
    assessmentLinkType: "owned",
    assessmentLinkShareType: "both",
    ShowOwnedSharedAssessments: true,
    sortField: "createdDate",
  })
  const [additionalFilters, setAdditionalFilters] = useState({
    owned: {},
    shared: {},
    system: {},
  })

  const topScroll = useRef()

  const graphQlClient = useApolloClient()

  const [getOrganization] = useLazyQuery(GET_ORGANIZATION, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: "-12",
      organization_id: "",
    },
  })

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      if (
        userCtx.session.user.enterprise_id ||
        selectedOrganization.organization_id
      ) {
        const useAdditionalFilters = getActiveAdditionalFilters()

        let useFilters = {
          ...listFilters,
          ...useAdditionalFilters,
          activeAssessments:
            listFilters.assessmentLinkType === "owned"
              ? listFilters.activeAssessments
              : true,
          organizationId: selectedOrganization.organization_id,
          enterpriseId: userCtx.session.user.enterprise_id, //Always include enterprise
          pageSize: pageSetup.rows,
          pageNum: pageSetup.page + 1,
        }

        const results = await assessmentsApiListPost(
          userCtx.tokens.getAssessmentsToken(),
          {
            ...useFilters,
          },
        )

        setAssessmentData(results)

        if (results?.assessments) {
          let orgs = [
            ...new Set(
              results.assessments.map(
                (assessment) => assessment.organizationId,
              ),
            ),
          ]
          //Check for any unmapped organizations
          orgs = orgs.filter(
            (org) =>
              !sharingOrganizations.some(
                (organization) => organization.organization_id === org,
              ),
          )

          if (orgs.length > 0) {
            try {
              const promises = orgs.map(async (organizationId) => {
                try {
                  const organization = await getOrganization({
                    variables: {
                      staff_id: "-12",
                      organization_id: organizationId,
                    },
                  })

                  if (organization?.data?.organization) {
                    return organization?.data?.organization
                  } else {
                    LogError(
                      `Unable to load organization data for ${organizationId}`,
                      organization,
                    )
                    return {
                      organization_id: organizationId,
                      organization_name: "Unknown organization",
                    }
                  }
                } catch (error) {
                  LogError(
                    `Unable to load organization data for ${organizationId}`,
                    error,
                  )
                  return {
                    organization_id: organizationId,
                    organization_name: "Unknown organization",
                  }
                }
              })

              // Wait for all promises to resolve
              const orgResults = await Promise.all(promises)

              setSharingOrganizations([
                ...sharingOrganizations,
                ...orgResults.filter((org) => !!org),
              ])
            } catch (error) {
              LogError("Unable to load organization data", error)
            }
          }
        }

        if (!results?.isActive) {
          if (listFilters.assessmentLinkType !== "system") {
            setListFilters({ ...listFilters, assessmentLinkType: "system" })
          }
        } else if (
          listFilters.assessmentLinkType === "system" &&
          tabViewIndex !== 2
        ) {
          onChangeTabView(2)
        }
      } else {
        setAssessmentData(undefined)
      }
      setIsLoading(false)
    } catch (error) {
      LogError("Unable to load assessment data", error)
      userCtx.setNotificationError("Unable to load assessment data")
      setAssessmentData(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listFilters,
    additionalFilters,
    pageSetup,
    userCtx.session.user.enterprise_id,
    selectedOrganization.organization_id,
  ])

  const [getOrganizations] = useLazyQuery(GET_ORGANIZATIONS_BY_ENTERPRISE, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      enterprise_id: userCtx.session.user.enterprise_id,
    },
  })

  const [getUsersFromList] = useLazyQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
    variables: {
      list: [],
    },
  })

  const loadOrganizationData = useCallback(async () => {
    try {
      //Retrieve organizations with service user to include enterprise organization
      const organizations = await getOrganizations({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          enterprise_id: userCtx.session.user.enterprise_id,
        },
      })

      if (organizations.data) {
        const assessmentActiveOrganizations =
          await assessmentsApiActiveOrganizationsGet(
            userCtx.tokens.getAssessmentsToken(),
            userCtx.session.user.enterprise_id,
          )

        const activeOrganizations =
          organizations.data.organizationsByEnterpriseUid.filter(
            (organization) =>
              organization.organization_status === "ACTIVE" &&
              assessmentActiveOrganizations?.includes(
                organization.organization_id,
              ),
          )

        setActiveOrganizations(activeOrganizations)
        setEnterpriseOrganizations(
          organizations.data.organizationsByEnterpriseUid.filter(
            (organization) => organization.organization_status === "ACTIVE",
          ),
        )
      } else {
        setActiveOrganizations(undefined)
        setEnterpriseOrganizations([
          {
            organization_id: userCtx.session.user.organization_id,
            organization_alias: userCtx.session.user.organization_alias,
            organization_name: userCtx.session.user.organization_name,
          },
        ])
        userCtx.setNotificationError(
          "Unable to load organizations for this enterprise.",
        )
      }
    } catch (error) {
      LogError("Unable to load organizations for this enterprise.", error)
      userCtx.setNotificationError(
        "Unable to load organizations for this enterprise.",
      )
      setActiveOrganizations(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.enterprise_id])

  const loadUsers = async (users) => {
    try {
      if (users && users.length > 0) {
        const userIds = users.map((user) => user.userId)

        const soarUsers = await getUsersFromList({
          variables: {
            list: userIds,
          },
        })

        //Filter out any staff profiles that do not match the expected organizations
        let distinctUsers = []

        let userList = soarUsers?.data?.usersFromList || []

        userList.forEach((userProfile) => {
          if (
            !distinctUsers.some(
              (users) => users.staff_id === userProfile.staff_id,
            )
          ) {
            distinctUsers = [...distinctUsers, userProfile]
          }
        })

        return distinctUsers
      } else {
        return []
      }
    } catch (error) {
      LogError("Unable to load assessment users", error)
      setUsers({ ownedUserIds: [], sharedUserIds: [], systemUserIds: [] })
    }
  }

  const loadUsersFromList = useCallback(async () => {
    try {
      if (selectedOrganization.organization_id) {
        let userList = await assessmentsApiGetUsers(
          userCtx.tokens.getAssessmentsToken(),
          userCtx.session.user.enterprise_id,
          selectedOrganization.organization_id,
        )

        if (userList) {
          setIsLoadingUsers(true)
          let orgUsers = await loadUsers(userList.ownedUserIds)
          let sharedUsers = await loadUsers(userList.sharedUserIds)
          let systemUsers = await loadUsers(userList.SystemUserIds)

          setUsers({
            ownedUserIds: orgUsers,
            sharedUserIds: sharedUsers,
            systemUserIds: systemUsers,
          })
        } else {
          setUsers({ ownedUserIds: [], sharedUserIds: [], systemUserIds: [] })
        }

        setIsLoadingUsers(false)
      }
    } catch (error) {
      LogError("Unable to load assessment users", error)
      setUsers({ ownedUserIds: [], sharedUserIds: [], systemUserIds: [] })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization.organization_id])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    loadUsersFromList()
  }, [loadUsersFromList])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    loadOrganizationData()
  }, [loadOrganizationData])

  const onDeleteAssessment = async (id) => {
    setShowFormDialog(false)
    setIsLoading(true)
    try {
      await assessmentsApiDelete(userCtx.tokens.getAssessmentsToken(), {
        assessmentId: id,
        userId: userCtx.session.user.staff_id,
      })
      setEditAssessmentId(null)
      loadData()
    } catch (error) {
      userCtx.setNotificationError(
        "Unable to delete assessment, please see console for more information",
      )
      setIsLoading(false)
      setEditAssessmentId(id)
      setShowFormDialog(true)
    }
  }

  const onChangeTabView = (tabIndex) => {
    if (tabViewIndex !== tabIndex) {
      setTabViewIndex(tabIndex)
      let tabFilter = "owned"

      switch (tabIndex) {
        case 1:
          tabFilter = "shared"
          break
        case 2:
          tabFilter = "system"
          break
        default:
          tabFilter = "owned"
          break
      }

      if (pageSetup.page !== 0) {
        setPageSetup({ ...pageSetup, first: 0, page: 0 })
      }
      setListFilters({
        ...listFilters,
        assessmentLinkType: tabFilter,
      })
    }
  }

  useEffect(() => {
    if (topScroll.current) {
      topScroll.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [topScroll])

  const getActiveUserList = () => {
    switch (tabViewIndex) {
      case 0:
        return users?.ownedUserIds || []
      case 1:
        return users?.sharedUserIds || []
      case 2:
        return users?.systemUserIds || []
      default:
        return []
    }
  }

  function getActiveAdditionalFilters() {
    switch (tabViewIndex) {
      case 0: //owned
        return additionalFilters.owned || {}
      case 1: //shared
        return additionalFilters.shared || {}
      case 2: //system
        return additionalFilters.system || {}
      default:
        return {}
    }
  }

  function updateActiveAdditionalFilters(filters) {
    const newAdditionalFilters = { ...additionalFilters }

    switch (tabViewIndex) {
      case 0: //owned
        newAdditionalFilters.owned = filters
        break
      case 1: //shared
        newAdditionalFilters.shared = filters
        break
      case 2: //system
        newAdditionalFilters.system = filters
        break
      default:
        break
    }

    setAdditionalFilters(newAdditionalFilters)
  }

  const OrganizationSelectorTemplate = (option) => {
    if (option) {
      return (
        <OrganizationTemplate
          organizationName={option.organization_name}
          organizationId={option.organization_id}
        />
      )
    } else if (selectedOrganization) {
      return (
        <OrganizationTemplate
          organizationId={selectedOrganization.organization_id}
          organizationName={selectedOrganization.organization_name}
        />
      )
    } else {
      return null
    }
  }

  const OrganizationTemplate = ({ organizationId, organizationName }) => {
    const isActiveOrg = activeOrganizations?.find(
      (org) => org.organization_id === organizationId,
    )
    const statusClass = isActiveOrg
      ? myStyles.organizationSelectorItemActive
      : myStyles.organizationSelectorItemDisabled

    const tooltip = isActiveOrg
      ? "Assessments+ active"
      : "Assessments+ disabled"

    return (
      <div className={myStyles.organizationSelectorItem}>
        <Tooltip target=".tt-organizationItem" />
        <div
          className={`${statusClass} tt-organizationItem`}
          data-pr-tooltip={tooltip}
          data-pr-position="bottom"
        >
          <span className="material-icons">add</span>
        </div>
        {organizationName}
      </div>
    )
  }

  return (
    <div ref={topScroll}>
      <PageSidebarContent>
        <EnterpriseLibrarySidebar />
      </PageSidebarContent>
      {showFilterDialog ? (
        <AssessmentListFilters
          filters={getActiveAdditionalFilters()}
          users={getActiveUserList()}
          onSetFilters={(filters) => {
            updateActiveAdditionalFilters(filters)
            setShowFilterDialog(false)
          }}
          onClose={() => {
            setShowFilterDialog(false)
          }}
        />
      ) : null}
      {showFormDialog ? (
        <ManagementForm
          assessmentProfileId={editAssessmentId}
          activeOrganizationId={selectedOrganization.organization_id}
          activeOrganizationAlias={selectedOrganization.organization_alias}
          organizations={activeOrganizations}
          onCancel={() => {
            setEditAssessmentId(undefined)
            setShowFormDialog(false)
          }}
          onDelete={onDeleteAssessment}
          onSaved={() => {
            setEditAssessmentId(undefined)
            setShowFormDialog(false)
            loadData()
          }}
        />
      ) : null}
      {previewAssessmentId ? (
        <AssessmentForm
          assessmentId={previewAssessmentId}
          organizationId={selectedOrganization.organization_id}
          organizationAlias={selectedOrganization.organization_alias}
          showForm={true}
          readOnly={false}
          previewMode={true}
          onCancel={() => {
            setPreviewAssessmentId(undefined)
          }}
        />
      ) : null}
      {cloneAssessment ? (
        <AssessmentClone
          assessmentId={cloneAssessment?.id}
          assessmentName={cloneAssessment?.name}
          activeOrganizationId={selectedOrganization.organization_id}
          activeOrganizationAlias={selectedOrganization.organization_alias}
          onClose={() => {
            setCloneAssessment(undefined)
          }}
          onCloned={() => {
            setCloneAssessment(undefined)
            onChangeTabView(0)
          }}
        />
      ) : null}
      <div className="main-content">
        <div className={myStyles.listLayout}>
          <div className={myStyles.listLayoutHeader}>
            <h1>Assessments for </h1>
            <Dropdown
              options={enterpriseOrganizations}
              value={selectedOrganization?.organization_id}
              optionLabel="organization_name"
              optionValue="organization_id"
              valueTemplate={OrganizationSelectorTemplate}
              itemTemplate={OrganizationSelectorTemplate}
              placeholder={
                selectedOrganization
                  ? selectedOrganization.organization_name
                  : "Select an organization"
              }
              onChange={(e) => {
                if (e.value) {
                  const newOrganization = enterpriseOrganizations.find(
                    (org) =>
                      org.organization_id ===
                      (e.value || userCtx.session.user.organization_id),
                  )

                  const newIsActive = activeOrganizations?.find(
                    (org) =>
                      org.organization_id === newOrganization.organization_id,
                  )
                  const oldWasActive = activeOrganizations?.find(
                    (org) =>
                      org.organization_id ===
                      selectedOrganization.organization_id,
                  )

                  if (newIsActive !== oldWasActive) {
                  }

                  setSelectedOrganization(newOrganization)
                }
              }}
              showClear={
                selectedOrganization?.organization_id !==
                userCtx.session.user.organization_id
              }
              filter
              className={`${myStyles.organizationSelector}`}
            />
          </div>
          {isLoading && assessmentData === undefined ? (
            <LoaderFullPage />
          ) : (
            <>
              <div className={myStyles.listLayoutAddButton}>
                {assessmentData?.isActive ? (
                  <Button
                    label="New Assessment"
                    onClick={() => {
                      setShowFormDialog(true)
                    }}
                    disabled={isLoading}
                    className={formStyles.button}
                  />
                ) : null}
              </div>
              <div className={myStyles.listLayoutBody}>
                <div className={myStyles.tabView}>
                  {assessmentData?.isActive ? (
                    <TabView
                      className={`formTabView`}
                      activeIndex={tabViewIndex}
                      onTabChange={(e) => {
                        onChangeTabView(e.index)
                      }}
                    >
                      <TabPanel header={"Owned"} disabled={isLoading}>
                        <AssessmentListHeader
                          inactiveCount={
                            assessmentData?.inactiveAssessmentCount
                          }
                          filters={listFilters}
                          additionalFilterCount={
                            Object.keys(additionalFilters.owned).length
                          }
                          onFiltersChange={(newFilters) => {
                            setListFilters(newFilters)
                          }}
                          onShowFilters={() => {
                            setShowFilterDialog(true)
                          }}
                          disabled={isLoading}
                        />
                        {isLoading ? (
                          <LoaderMedium />
                        ) : (
                          <AssessmentListTable
                            dataSet={assessmentData}
                            activeOrganization={
                              selectedOrganization.organization_id
                            }
                            isLoadingUsers={isLoadingUsers}
                            users={users.ownedUserIds}
                            displayType={listFilters.assessmentLinkType}
                            onEdit={(assessmentId) => {
                              setEditAssessmentId(assessmentId)
                              setShowFormDialog(true)
                            }}
                            onSetPageSetup={setPageSetup}
                            onPreviewAssessment={(assessmentId) => {
                              setPreviewAssessmentId(assessmentId)
                            }}
                          />
                        )}
                      </TabPanel>
                      <TabPanel header={"Shared"} disabled={isLoading}>
                        <AssessmentListHeader
                          hideActiveSelector={true}
                          showAccessLevelToggles={true}
                          filters={listFilters}
                          additionalFilterCount={
                            Object.keys(additionalFilters.shared).length
                          }
                          onFiltersChange={(newFilters) => {
                            setListFilters(newFilters)
                          }}
                          onShowFilters={() => {
                            setShowFilterDialog(true)
                          }}
                          disabled={isLoading}
                        />
                        {isLoading ? (
                          <LoaderMedium />
                        ) : (
                          <AssessmentListTable
                            dataSet={assessmentData}
                            isLoadingUsers={isLoadingUsers}
                            users={users.sharedUserIds}
                            activeOrganization={
                              selectedOrganization.organization_id
                            }
                            organizations={sharingOrganizations}
                            displayType={listFilters.assessmentLinkType}
                            onEdit={(assessmentId) => {
                              setEditAssessmentId(assessmentId)
                              setShowFormDialog(true)
                            }}
                            onSetPageSetup={setPageSetup}
                            onClone={(id, name) => {
                              setCloneAssessment({ id: id, name: name })
                            }}
                            onPreviewAssessment={(assessmentId) => {
                              setPreviewAssessmentId(assessmentId)
                            }}
                          />
                        )}
                      </TabPanel>
                      <TabPanel header={"System"} disabled={isLoading}>
                        <AssessmentListHeader
                          hideActiveSelector={true}
                          showSytemTabHelptext={true}
                          filters={listFilters}
                          additionalFilterCount={
                            Object.keys(additionalFilters.system).length
                          }
                          onFiltersChange={(newFilters) => {
                            setListFilters(newFilters)
                          }}
                          onShowFilters={() => {
                            setShowFilterDialog(true)
                          }}
                          disabled={isLoading}
                        />
                        {isLoading ? (
                          <LoaderMedium />
                        ) : (
                          <AssessmentListTable
                            dataSet={assessmentData}
                            activeOrganization={
                              selectedOrganization.organization_id
                            }
                            isLoadingUsers={isLoadingUsers}
                            users={users.systemUserIds}
                            displayType={listFilters.assessmentLinkType}
                            onEdit={(assessmentId) => {
                              setEditAssessmentId(assessmentId)
                              setShowFormDialog(true)
                            }}
                            onSetPageSetup={setPageSetup}
                            onClone={(id, name) => {
                              setCloneAssessment({ id: id, name: name })
                            }}
                            onPreviewAssessment={(assessmentId) => {
                              setPreviewAssessmentId(assessmentId)
                            }}
                          />
                        )}
                      </TabPanel>
                    </TabView>
                  ) : (
                    <TabView
                      className={`formTabView`}
                      activeIndex={0}
                      onTabChange={() => {}}
                    >
                      <TabPanel header={"System"} disabled={isLoading}>
                        <AssessmentListHeader
                          hideActiveSelector={true}
                          activeOrganization={
                            selectedOrganization.organization_id
                          }
                          showSytemTabHelptext={true}
                          filters={listFilters}
                          additionalFilterCount={
                            Object.keys(additionalFilters.system).length
                          }
                          onFiltersChange={(newFilters) => {
                            setListFilters(newFilters)
                          }}
                          onShowFilters={() => {
                            setShowFilterDialog(true)
                          }}
                          disabled={isLoading}
                        />
                        {isLoading ? (
                          <LoaderMedium />
                        ) : (
                          <AssessmentListTable
                            dataSet={assessmentData}
                            isLoadingUsers={isLoadingUsers}
                            users={users.systemUserIds}
                            activeOrganization={
                              selectedOrganization.organization_id
                            }
                            displayType={listFilters.assessmentLinkType}
                            onEdit={(assessmentId) => {
                              setEditAssessmentId(assessmentId)
                              setShowFormDialog(true)
                            }}
                            onSetPageSetup={setPageSetup}
                            onClone={(id, name) => {
                              setCloneAssessment({ id: id, name: name })
                            }}
                            onPreviewAssessment={(assessmentId) => {
                              setPreviewAssessmentId(assessmentId)
                            }}
                          />
                        )}
                      </TabPanel>
                    </TabView>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
